import { type AdminClient } from '@apx/admin-sdk';
import { fmtNumber } from '../format.js';
import { icon } from '../icons.js';

export interface LiveRtpCardDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const LOW_VOLUME_STAKE = 1_000_000n;

function pct(v: number | null): string {
  return v === null ? '—' : `${(v * 100).toFixed(1)}%`;
}

function rtpColor(v: number | null): string {
  if (v === null) return 'var(--muted)';
  if (v >= 1.0) return 'var(--error)';
  if (v >= 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/**
 * Live RTP card (real settled rounds, last 24h) with a Refresh button. Returns
 * the card element; the host appends it. Failure is isolated to the card.
 */
export function renderLiveRtpCard(deps: LiveRtpCardDeps): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card';
  card.innerHTML = `
    <div class="row" style="justify-content:space-between;align-items:center">
      <h2 style="margin:0">Live RTP — last 24h <span class="muted" style="font-size:12px;font-weight:400">(real settled rounds)</span></h2>
      <button class="ghost" type="button" data-rtp-refresh>Refresh</button>
    </div>
    <div class="muted" style="margin-top:8px" data-rtp-body>Loading…</div>
  `;
  const body = card.querySelector<HTMLElement>('[data-rtp-body]')!;

  async function loadRtp(): Promise<void> {
    body.innerHTML = '<span class="muted">Loading…</span>';
    try {
      const r = await deps.client.getRtp(deps.integratorId, deps.gameId, 24);
      const lowVol = BigInt(r.staked || '0') < LOW_VOLUME_STAKE;
      const rows = r.perBetType
        .map(
          (b) =>
            `<tr><td>${b.betType}</td><td style="text-align:right">${b.bets}</td>` +
            `<td style="text-align:right;color:${rtpColor(b.rtp)}">${pct(b.rtp)}</td></tr>`,
        )
        .join('');
      body.innerHTML = `
        <div class="row" style="gap:24px;align-items:baseline;flex-wrap:wrap">
          <div><span class="muted">Overall RTP</span>
            <div style="font-size:28px;font-weight:700;color:${rtpColor(r.rtp)}">${pct(r.rtp)}</div></div>
          <div><span class="muted">Rounds</span><div style="font-size:18px">${r.rounds}</div></div>
          <div><span class="muted">Staked</span><div style="font-size:18px">${fmtNumber(r.staked)}</div></div>
        </div>
        ${lowVol ? `<p class="muted" style="margin:8px 0 0;display:inline-flex;align-items:center;gap:5px">${icon('warn', { size: 13 })}Low volume — RTP is statistically noisy until more bets land.</p>` : ''}
        <table class="data-table" style="margin-top:10px"><thead><tr><th>Bet type</th><th style="text-align:right">Bets</th><th style="text-align:right">RTP</th></tr></thead><tbody>${rows || '<tr><td colspan="3" class="muted">No bets in window</td></tr>'}</tbody></table>`;
    } catch (err) {
      body.innerHTML = `<span style="color:var(--error)">Failed to load RTP: ${err instanceof Error ? err.message : String(err)}</span>`;
    }
  }

  card.querySelector<HTMLButtonElement>('[data-rtp-refresh]')?.addEventListener('click', () => void loadRtp());
  void loadRtp();
  return card;
}
